import {
  Autocomplete,
  TextField,
  Box,
  Chip,
  Skeleton,
  Tooltip,
  InputAdornment,
  Button,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePartyManager } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  EDIT_SOURCE,
  CODEMETA_SOURCE,
} from './partyManagerUtils';

const EmployeeSelector = ({ label }) => {
  const {
    partyData,
    personOptions,
    isUIBlocked,
    isPartyManagerLoading,
    handleEmployeeChange,
    handleCycleSource,
    getAvailableSources,
    contactWarnings,
    handleTransferContactsToEdit,
    getCustomOptionLabel,
    renderCustomOption,
    isSearchingPersons,
    handlePersonSearch,
    selectedPersonValue,
  } = usePartyManager();

  const fieldKey = 'contacts';
  const fieldState = partyData[fieldKey];

  if (!fieldState) {
    return null;
  }

  const selectedSource = fieldState.selected || 'edit';
  const isLocked = selectedSource !== 'edit';
  const availableSources = getAvailableSources(fieldKey);

  const getButtonIcon = () => {
    if (selectedSource === 'edit') return EDIT_SOURCE.icon;
    if (selectedSource === 'codemeta') {
      return (
        <img
          src={CODEMETA_SOURCE.icon}
          alt={CODEMETA_SOURCE.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    const sourceInfo = POSSIBLE_SOURCES[selectedSource];
    if (sourceInfo?.icon) {
      return (
        <img
          src={sourceInfo.icon}
          alt={sourceInfo.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    return '?';
  };
  const value = selectedPersonValue;

  const warnings = Object.values(contactWarnings).flat();
  const warningTitle =
    warnings.length > 0 ? (
      <div>
        <p>Folgende Personen wurden nicht gefunden:</p>
        <ul>
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      </div>
    ) : (
      ''
    );

  const tooltipTitle = isLocked
    ? 'Klicken, um diese Auswahl zur Bearbeitung zu übernehmen.'
    : '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
      }}
    >
      {isPartyManagerLoading ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <>
          <Tooltip title={tooltipTitle} placement="top-start" arrow>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: isLocked ? 'pointer' : 'default',
              }}
              onClick={isLocked ? handleTransferContactsToEdit : undefined}
            >
              <Autocomplete
                multiple
                id="employee-select"
                fullWidth
                options={personOptions}
                size="small"
                value={value}
                onChange={(event, newValue) => {
                  handleEmployeeChange(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  if (!isLocked) {
                    handlePersonSearch(newInputValue);
                  }
                }}
                loading={isSearchingPersons}
                loadingText="Suche..."
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, val) => option._id === val._id}
                getOptionLabel={getCustomOptionLabel}
                renderOption={renderCustomOption}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        variant="outlined"
                        size="small"
                        label={`${option.firstName ?? ''} ${
                          option.lastName ?? ''
                        }`.trim()}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    size="small"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87)',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.38)',
                        '&.MuiInputLabel-shrink': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {warnings.length > 0 && (
                            <InputAdornment position="end">
                              <Tooltip title={warningTitle}>
                                <WarningAmberIcon color="warning" />
                              </Tooltip>
                            </InputAdornment>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={isUIBlocked || isLocked}
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              />
            </Box>
          </Tooltip>
          {availableSources.length > 1 && (
            <Button
              title={`Quelle wechseln: Aktuell ${selectedSource}.`}
              sx={{ width: '40px', height: '40px', minWidth: '40px' }}
              onClick={() => handleCycleSource(fieldKey)}
              variant="outlined"
            >
              {getButtonIcon()}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default EmployeeSelector;
